/**
 * EXPERIMENTAL native Studio MCP connector creation, observed 2026-09-06.
 * Fixed wizard contract, not a documented public API or a connection/grant API.
 * O(request bytes + bounded response bytes); no retry or follow-on tenant write.
 */
import { z } from "zod";
import type { AuthProvider } from "../auth/types.js";
import { getSafeCopilotStudioManagementAuthReason } from "../auth/copilot-studio-auth-error.js";
import type { EnvironmentApi } from "./environment.js";
import { requestWithRetry } from "../utils/http.js";
import { ApiError } from "../utils/errors.js";
import { GUID_PATTERN, isPrivateHost } from "../utils/security.js";

const DEADLINE_MS = 15000;
const MAX_RESPONSE_BYTES = 1024 * 1024;
export const COPILOT_MCP_ENVIRONMENT_PATTERN =
  /^(?:Default-)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CONNECTOR_ID = /^\/providers\/Microsoft\.PowerApps\/apis\/[A-Za-z][A-Za-z0-9_-]{1,255}$/;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/;

/** Public-host syntax only: no DNS, metadata discovery or resource request occurs here. */
function resourceUrl(value: string): boolean {
  if (value.length > 2048 || CONTROL.test(value) || /[\\?#%]/.test(value)) return false;
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      !url.port &&
      value === `${url.origin}${url.pathname}` &&
      /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(url.hostname) &&
      !isPrivateHost(url.hostname) &&
      !/\.(?:localhost|test|invalid|example|home|lan)$/.test(url.hostname) &&
      /^\/[A-Za-z0-9/_~.!$&'()+,;=:@-]*$/.test(url.pathname)
    );
  } catch {
    return false;
  }
}

export const createCopilotMcpConnectorInputSchema = z
  .object({
    environment: z.string().regex(COPILOT_MCP_ENVIRONMENT_PATTERN),
    displayName: z
      .string()
      .min(1)
      .max(100)
      .refine((v) => v.trim() === v && !CONTROL.test(v)),
    description: z
      .string()
      .max(2000)
      .refine((v) => !CONTROL.test(v)),
    resourceUrl: z
      .string()
      .refine(
        resourceUrl,
        "A canonical HTTPS public-host URL without credentials, query or fragment is required.",
      ),
    solutionId: z.string().regex(GUID_PATTERN),
    referenceId: z.string().regex(GUID_PATTERN),
    confirmCreate: z.boolean().optional(),
  })
  .strict();
export type CreateCopilotMcpConnectorInput = z.infer<typeof createCopilotMcpConnectorInputSchema>;

export function buildCopilotMcpConnectorBlueprint(input: CreateCopilotMcpConnectorInput) {
  const parsed = createCopilotMcpConnectorInputSchema.parse(input);
  const resource = new URL(parsed.resourceUrl);
  return {
    iconUri: "",
    swaggerContract: {
      swagger: "2.0",
      info: { title: parsed.displayName, description: parsed.description, version: "1.0.0" },
      host: resource.host,
      basePath: "/",
      schemes: ["https"],
      paths: {
        [resource.pathname]: {
          post: {
            responses: { "200": { description: "Immediate Response" } },
            "x-ms-agentic-protocol": "mcp-streamable-1.0",
            operationId: "InvokeServer",
            summary: parsed.displayName,
            description: parsed.description,
          },
        },
      },
      securityDefinitions: {},
      security: [],
    },
    description: parsed.description,
    displayName: parsed.displayName,
    solutionId: parsed.solutionId,
    referenceId: parsed.referenceId,
    connectionParameters: {
      token: {
        type: "oauthSetting",
        oAuthSettings: {
          identityProvider: "oauth2pkcewithprm",
          redirectMode: "globalPerConnector",
          customParameters: { ServerUrl: { value: parsed.resourceUrl } },
        },
      },
    },
  };
}

type Failure =
  | "invalid-input"
  | "authentication"
  | "preflight"
  | "identity"
  | "deadline"
  | "http"
  | "transport"
  | "unavailable";
const REASONS: Record<Failure, string> = {
  "invalid-input": "invalid input or explicit create confirmation missing",
  authentication: "same-account silent Studio management authentication unavailable",
  preflight: "environment discovery unavailable or unsupported",
  identity: "selected account identity changed or is unavailable",
  deadline: "deadline exceeded",
  http: "service did not return the observed creation acknowledgement",
  transport: "request outcome unavailable",
  unavailable: "API capability unavailable",
};

/** Authored diagnostics only; never accepts upstream error messages, headers or bodies. */
export class CopilotMcpConnectorCreateError extends ApiError {
  readonly createMayHaveOccurred: boolean;
  readonly authenticationReason: string | null;
  constructor(
    reason: Failure,
    createMayHaveOccurred = false,
    statusCode?: number,
    authenticationError?: unknown,
  ) {
    const status =
      Number.isInteger(statusCode) && statusCode! >= 100 && statusCode! <= 599 ? statusCode! : 502;
    const authenticationReason =
      reason === "authentication"
        ? getSafeCopilotStudioManagementAuthReason(authenticationError)
        : null;
    super(
      `Copilot MCP connector creation: ${REASONS[reason]}${authenticationReason === null ? "" : ` [${authenticationReason}]`}${reason === "http" ? ` (HTTP ${status})` : ""}. ${createMayHaveOccurred ? "Creation may have occurred; inspect existing connectors before another change." : "No connector create request was sent."} No automatic retry or fallback was attempted.`,
      status,
    );
    this.name = "CopilotMcpConnectorCreateError";
    this.createMayHaveOccurred = createMayHaveOccurred;
    this.authenticationReason = authenticationReason;
  }
}

function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function accepted(input: CreateCopilotMcpConnectorInput, connectorId: string | null) {
  return {
    experimental: true as const,
    status: connectorId ? ("accepted" as const) : ("accepted-unconfirmed" as const),
    httpStatus: 201 as const,
    environment: input.environment,
    displayName: input.displayName,
    resourceUrl: input.resourceUrl,
    solutionId: input.solutionId,
    referenceId: input.referenceId,
    connectorId,
    responseIdentityMatched: connectorId !== null,
    independentReadbackVerified: false as const,
    provider: "oauth2pkcewithprm" as const,
    notice:
      "HTTP 201 acknowledges creation, not independently verified stored configuration, authentication, grants, binding or runtime success. Inspect existing connector metadata before another create; referenceId is not proven to provide idempotency. No connection, agent binding, publication or evaluation was requested.",
  };
}

export class CopilotMcpConnectorApi {
  constructor(
    private readonly authProvider: AuthProvider,
    private readonly environmentApi: Pick<EnvironmentApi, "getEnvironment">,
  ) {}

  async createConnector(input: CreateCopilotMcpConnectorInput) {
    const validation = createCopilotMcpConnectorInputSchema.safeParse(input);
    if (!validation.success || validation.data.confirmCreate !== true)
      throw new CopilotMcpConnectorCreateError("invalid-input");
    const parsed = validation.data;
    const payload = JSON.stringify(buildCopilotMcpConnectorBlueprint(parsed));
    if (Buffer.byteLength(payload) > 16384)
      throw new CopilotMcpConnectorCreateError("invalid-input");
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let body: Awaited<ReturnType<typeof requestWithRetry>>["body"] | undefined;
    const safeDestroy = () => {
      try {
        body?.destroy();
      } catch {
        // Cleanup must not replace a fixed diagnostic or prevent deadline settlement.
      }
    };
    let sent = false;
    let acknowledged = false;
    const fail = (reason: Failure, status?: number, authenticationError?: unknown) =>
      new CopilotMcpConnectorCreateError(reason, sent, status, authenticationError);
    const active = () => {
      if (controller.signal.aborted) {
        safeDestroy();
        throw fail("deadline");
      }
    };
    const work = async () => {
      // Test this optional capability before discovery or any authentication request.
      if (!this.authProvider.getCopilotStudioTokenSilent) throw fail("authentication");
      const before = this.authProvider.getCurrentAccount();
      if (
        !before ||
        typeof before.username !== "string" ||
        !before.username.trim() ||
        typeof before.tenantId !== "string" ||
        !GUID_PATTERN.test(before.tenantId) ||
        (before.localAccountId !== undefined &&
          (typeof before.localAccountId !== "string" || !GUID_PATTERN.test(before.localAccountId)))
      )
        throw fail("identity");
      const selected = {
        username: before.username,
        tenantId: before.tenantId,
      };
      // Azure CLI initially exposes username/tenant only. Its existing silent
      // management method validates and hydrates oid; do not invent it here.
      let selectedObjectId: string | undefined = before.localAccountId;
      const sameAccount = (requireObjectId = true) => {
        const now = this.authProvider.getCurrentAccount();
        if (
          !now ||
          now.username !== selected.username ||
          now.tenantId !== selected.tenantId ||
          (now.localAccountId !== undefined &&
            (typeof now.localAccountId !== "string" || !GUID_PATTERN.test(now.localAccountId))) ||
          (selectedObjectId !== undefined && now.localAccountId !== selectedObjectId) ||
          (requireObjectId && now.localAccountId === undefined)
        )
          throw fail("identity");
        if (now.localAccountId !== undefined) selectedObjectId = now.localAccountId;
      };
      let env: unknown;
      try {
        env = await this.environmentApi.getEnvironment(parsed.environment, {
          boundedRead: true,
          signal: controller.signal,
        });
      } catch {
        throw fail("preflight");
      }
      active();
      if (
        !object(env) ||
        typeof env.name !== "string" ||
        env.name.toLowerCase() !== parsed.environment.toLowerCase() ||
        !object(env.properties) ||
        !object(env.properties.runtimeEndpoints)
      )
        throw fail("preflight");
      const endpoint = env.properties.runtimeEndpoints["microsoft.PowerVirtualAgents"];
      if (
        typeof endpoint !== "string" ||
        endpoint.length > 512 ||
        !/^https:\/\/powervamg\.[a-z0-9-]+\.gateway\.prod\.island\.powerapps\.com\/?$/.test(
          endpoint,
        )
      )
        throw fail("preflight");
      sameAccount(false);
      let token: unknown;
      try {
        token = await this.authProvider.getCopilotStudioTokenSilent();
      } catch (error) {
        throw fail("authentication", undefined, error);
      }
      active();
      sameAccount();
      if (typeof token !== "string" || !token.trim() || /[\r\n]/.test(token))
        throw fail("authentication");
      const url = `${new URL(endpoint).origin}/api/botmanagement/v1/environments/${encodeURIComponent(parsed.environment)}/connectors/apim`;
      sent = true;
      const response = await requestWithRetry(
        url,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
            "x-cci-tenantid": selected.tenantId,
          },
          body: payload,
          signal: controller.signal,
          headersTimeout: DEADLINE_MS,
          bodyTimeout: DEADLINE_MS,
          // No redirect interceptor is installed; 3xx responses are returned.
        },
        { maxAttempts: 1 },
      );
      body = response.body;
      acknowledged = response.statusCode === 201;
      active();
      if (!acknowledged) throw fail("http", response.statusCode);
      sameAccount();
      const contentType = response.headers["content-type"];
      if (
        typeof contentType !== "string" ||
        !/^application\/(?:json|[a-z0-9.+-]+\+json)(?:\s*;|$)/i.test(contentType)
      )
        return accepted(parsed, null);
      const chunks: Buffer[] = [];
      let size = 0;
      for await (const chunk of body as AsyncIterable<Uint8Array>) {
        active();
        const bytes = Buffer.from(chunk);
        size += bytes.length;
        if (size > MAX_RESPONSE_BYTES) return accepted(parsed, null);
        chunks.push(bytes);
      }
      active();
      sameAccount();
      let value: unknown;
      try {
        value = JSON.parse(Buffer.concat(chunks).toString("utf8"));
      } catch {
        return accepted(parsed, null);
      }
      if (
        !object(value) ||
        typeof value.connectorId !== "string" ||
        !CONNECTOR_ID.test(value.connectorId) ||
        value.displayName !== parsed.displayName ||
        (value.isCustom !== undefined && value.isCustom !== true)
      )
        return accepted(parsed, null);
      return accepted(parsed, value.connectorId);
    };
    try {
      // Silent token work cannot be cancelled; late completion cannot start a POST.
      return await Promise.race([
        work(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            safeDestroy();
            reject(fail("deadline"));
          }, DEADLINE_MS);
        }),
      ]);
    } catch (error) {
      if (acknowledged) return accepted(parsed, null);
      if (error instanceof CopilotMcpConnectorCreateError) throw error;
      throw fail("transport");
    } finally {
      clearTimeout(timer);
      controller.abort();
      safeDestroy();
    }
  }
}
